
import { useState, useCallback } from 'react';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useLocationExtraction = () => {
  const [locationInfo, setLocationInfo] = useState<string | null>(null);

  const extractLocationInfo = useCallback((post: BlogPost) => {
    const content = post.content.toLowerCase();
    const title = post.title.toLowerCase();
    
    // Check if this appears to be a location-specific post by looking for location indicators
    const hasBusinessInfo = content.includes('carolina trophy roofs') || 
                           content.includes('(252) 302-2003') ||
                           content.includes('rocky mount, nc');
    
    if (!hasBusinessInfo) {
      return null; // This is likely a template post
    }

    // First, check if the post has variable placeholders that haven't been replaced
    if (title.includes('{locationname}') || title.includes('{name}') || 
        content.includes('{locationname}') || content.includes('{name}')) {
      console.log(`Post contains unreplaced variables: "${post.title}"`);
      return null; // This is a template post with unreplaced variables
    }

    // Look for location patterns in title - more specific patterns
    const titleLocationPatterns = [
      // Look for "Location Church" or "Location Roofing" at start
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:Church|Roofing|Roof)/i,
      // Look for "in Location, NC"
      /\bin\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*(?:NC|North Carolina)/i,
      // Look for "Location, NC"
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*(?:NC|North Carolina)/i,
    ];

    for (const pattern of titleLocationPatterns) {
      const match = post.title.match(pattern);
      if (match && match[1]) {
        let location = match[1].trim();
        // Filter out common words that aren't locations
        const excludeWords = ['Your', 'The', 'Why', 'When', 'Faith', 'Meets', 'Engineering', 'Homeowners', 'Are', 'Switching'];
        if (!excludeWords.some(word => location.includes(word))) {
          console.log(`Extracted location from title: "${location}" from "${post.title}"`);
          return location;
        }
      }
    }

    // Check tags for location info
    if (post.tags) {
      for (const tag of post.tags) {
        if (tag.includes('-roofing') && !tag.includes('roofing-')) {
          const location = tag.replace('-roofing', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          console.log(`Extracted location from tag: "${location}"`);
          return location;
        }
      }
    }

    // Check categories for location variables
    if (post.categories) {
      for (const category of post.categories) {
        if (category.includes('{locationname}')) {
          console.log(`Post has location category variables but no location extracted: "${post.title}"`);
          return null; // This indicates it's a template
        }
      }
    }

    // Look for specific location names in content
    const contentLocationPatterns = [
      /serving\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /located\s+in\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+area/i,
    ];

    for (const pattern of contentLocationPatterns) {
      const match = post.content.match(pattern);
      if (match && match[1]) {
        let location = match[1].trim();
        if (location && location.length > 2) {
          console.log(`Extracted location from content: "${location}"`);
          return location;
        }
      }
    }

    console.log(`No location extracted for post: "${post.title}"`);
    return null;
  }, []);

  return {
    locationInfo,
    setLocationInfo,
    extractLocationInfo
  };
};
